import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ModalBasicLayout from "./ModalBasicLayout";
import Button from "../Buttons/Button";
import ShareModalMultiple from "../Gallery/ShareModalMultiple/ShareModalMultiple";
import {
  MODAL_SUCCESS_BRAND_SETUP_TITLE,
  MODAL_SUCCESS_BRAND_SETUP_SUBTITLE,
  MODAL_SUCCESS_BRAND_SETUP_OPTION_CAMPAIGN_TITLE,
  MODAL_SUCCESS_BRAND_SETUP_OPTION_CAMPAIGN_DESCRIPTION,
  MODAL_SUCCESS_BRAND_SETUP_OPTION_ADS_TITLE,
  MODAL_SUCCESS_BRAND_SETUP_OPTION_ADS_DESCRIPTION,
  MODAL_SUCCESS_BRAND_SETUP_OPTION_POST_TITLE,
  MODAL_SUCCESS_BRAND_SETUP_OPTION_CALENDAR_DESCRIPTION,
} from "../../i18n/keysTranslations";
import creativeContentCreationIllustration from "../../assets/img/content.png";
import digitalAdvertisingCampaignIllustration from "../../assets/img/ads.png";
import contentCalendarPlanningIllustration from "../../assets/img/calendar.png";
import { useDispatchApp } from "../../lib/redux";
import { getIsBlockedActions } from "../../actions/getters";
import { useCreditActionCosts } from "../../hooks/useCreditActionCosts";
import {
  GRAY_200_SOLID,
  GRAY_700_30_OPACITY,
  BOX_SHADOW_CARD,
  BOX_SHADOW_CARD_HOVER,
  BOX_SHADOW_BUTTON_CARD,
  GRADIENT_OVERLAY_CARD,
  WHITE_COLOR,
  GRADIENT_AI_CONTENT,
  GRADIENT_GENERATE_ADS,
  GRADIENT_POST_SOCIAL,
} from "../../utils/colors";
import { Sparkles, Megaphone, Calendar } from "lucide-react";

const ModalSuccessBrandSetup = ({ onCloseModal }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatchApp();
  const { ADS_CAMPAIGN_GENERATE_CAMPAIGN_ACTION } = useCreditActionCosts();
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const onOpenGallery = () => {
    onCloseModal();
    const galleryButton = document.getElementById("button-open-gallery");
    if (galleryButton) {
      galleryButton.click();
    }
  };

  const onOpenShareModal = () => {
    setIsShareModalOpen(true);
  };

  const onNavigateToCampaigns = async () => {
    onCloseModal();
    const isBlocked = await dispatch(
      getIsBlockedActions({
        action: ADS_CAMPAIGN_GENERATE_CAMPAIGN_ACTION,
        showUsageConfirmationAlert: false,
      })
    );
    if (!isBlocked) {
      navigate("/ads-campaign");
    }
  };

  const options = [
    {
      title: MODAL_SUCCESS_BRAND_SETUP_OPTION_ADS_TITLE,
      description: MODAL_SUCCESS_BRAND_SETUP_OPTION_ADS_DESCRIPTION,
      image: digitalAdvertisingCampaignIllustration,
      action: onOpenGallery,
      icon: Sparkles,
      gradient: GRADIENT_GENERATE_ADS,
    },
    {
      title: MODAL_SUCCESS_BRAND_SETUP_OPTION_POST_TITLE,
      description: MODAL_SUCCESS_BRAND_SETUP_OPTION_CALENDAR_DESCRIPTION,
      image: contentCalendarPlanningIllustration,
      action: onOpenShareModal,
      icon: Calendar,
      gradient: GRADIENT_POST_SOCIAL,
    },
    {
      title: MODAL_SUCCESS_BRAND_SETUP_OPTION_CAMPAIGN_TITLE,
      description: MODAL_SUCCESS_BRAND_SETUP_OPTION_CAMPAIGN_DESCRIPTION,
      image: creativeContentCreationIllustration,
      action: onNavigateToCampaigns,
      icon: Megaphone,
      gradient: GRADIENT_AI_CONTENT,
    },
  ];

  return (
    <>
      <ModalBasicLayout
        modalOpen={true}
        onCloseModal={onCloseModal}
        title=""
        maxWidth="md"
        fullWidth
        showCloseButton={true}
        hasTitle={true}
        sxTitle={{
          position: "absolute",
          right: -5,
          top: -5,
        }}
        sxContent={{
          p: 0,
          "&.MuiDialogContent-root": {
            padding: 0,
          },
        }}
        sxPaper={{
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            backgroundColor: WHITE_COLOR,
            px: 4,
            pt: 4,
            pb: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <Typography
            variant="h4"
            align="center"
            sx={{
              fontSize: "36px",
              fontWeight: 600,
            }}
          >
            {t(MODAL_SUCCESS_BRAND_SETUP_TITLE)}
          </Typography>
          <Typography
            variant="body2"
            align="center"
            sx={{
              fontSize: "16px",
              color: "text.secondary",
            }}
          >
            {t(MODAL_SUCCESS_BRAND_SETUP_SUBTITLE)}
          </Typography>
        </Box>

        {/* Options Grid */}
        <Box
          sx={{
            px: 3,
            pb: 3,
          }}
        >
          <Grid container spacing={2} sx={{ pb: 2, px: 1 }}>
            {options.map((option, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  onClick={option.action}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    borderRadius: "12px",
                    backgroundColor: WHITE_COLOR,
                    boxShadow: BOX_SHADOW_CARD,
                    border: `1px solid ${GRAY_200_SOLID}`,
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    height: "100%",
                    "&:hover": {
                      boxShadow: BOX_SHADOW_CARD_HOVER,
                      transform: "translateY(-6px)",
                      borderColor: GRAY_700_30_OPACITY,
                      "& .card-button": {
                        boxShadow: BOX_SHADOW_BUTTON_CARD,
                      },
                      "& .card-arrow": {
                        transform: "translateX(4px)",
                      },
                      "& .card-image": {
                        transform: "scale(1.1)",
                      },
                      "& .card-overlay": {
                        opacity: 1,
                      },
                    },
                  }}
                >
                  {/* Gradient and Icon */}
                  <Box
                    sx={{
                      position: "relative",
                      height: 144,
                      width: "100%",
                      overflow: "hidden",
                      background: option.gradient,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <option.icon
                      color="white"
                      size={55}
                      className="card-image"
                      style={{
                        transition: "transform 0.5s ease",
                      }}
                    />
                    <Box
                      className="card-overlay"
                      sx={{
                        position: "absolute",
                        inset: 0,
                        background: GRADIENT_OVERLAY_CARD,
                        opacity: 0,
                        transition: "opacity 0.3s ease",
                      }}
                    />
                  </Box>

                  {/* Content */}
                  <CardContent
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      justifyContent: "flex-start",
                      px: 1.5,
                      py: 2,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        fontSize: "16px",
                        color: "text.primary",
                        mb: 1,
                      }}
                    >
                      {t(option.title)}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.875rem",
                      }}
                    >
                      {t(option.description)}
                    </Typography>
                  </CardContent>

                  {/* Button */}
                  <Box
                    sx={{
                      px: 2,
                      pb: 2,
                    }}
                  >
                    <Button
                      className="card-button"
                      color="primary"
                      sx={{
                        height: 40,
                        width: "100%",
                      }}
                    >
                      {t(option.title)}
                      <ArrowForwardIcon
                        className="card-arrow"
                        sx={{
                          ml: 1,
                          fontSize: 16,
                          transition: "transform 0.3s ease",
                          color: "white !important",
                        }}
                      />
                    </Button>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </ModalBasicLayout>

      {isShareModalOpen && (
        <ShareModalMultiple
          modalOpen={isShareModalOpen}
          onCloseModal={() => setIsShareModalOpen(false)}
          item={null}
        />
      )}
    </>
  );
};

export default ModalSuccessBrandSetup;
